/**
 * NAV-szintu strukturalt cimkezelesi komponens
 */
import { useState, useEffect } from 'react';

export interface AddressData {
  postalCode: string;
  city: string;
  streetName: string;
  streetType: string;
  houseNumber: string;
  building: string;
  staircase: string;
  floor: string;
  door: string;
}

export const emptyAddress: AddressData = {
  postalCode: '',
  city: '',
  streetName: '',
  streetType: '',
  houseNumber: '',
  building: '',
  staircase: '',
  floor: '',
  door: '',
};

interface AddressFormProps {
  value: AddressData;
  onChange: (value: AddressData) => void;
  required?: boolean;
}

const STREET_TYPES = [
  'utca', 'út', 'tér', 'körút', 'köz', 'sétány', 'sor', 'liget',
  'park', 'dűlő', 'lakótelep', 'rakpart', 'fasor', 'sugárút',
  'lejtő', 'udvar', 'tanya', 'puszta', 'telep', 'árok', 'egyéb',
];

const KNOWN_STREET_TYPE_WORDS = STREET_TYPES.filter(t => t !== 'egyéb');

export function formatAddress(addr: AddressData): string {
  const parts: string[] = [];
  if (addr.postalCode) parts.push(addr.postalCode);
  if (addr.city) parts.push(addr.city + ',');

  const streetParts: string[] = [];
  if (addr.streetName) streetParts.push(addr.streetName);
  if (addr.streetType) streetParts.push(addr.streetType);
  if (addr.houseNumber) streetParts.push(addr.houseNumber);
  if (streetParts.length > 0) parts.push(streetParts.join(' '));

  let result = parts.join(' ');

  const extraParts: string[] = [];
  if (addr.building) extraParts.push(addr.building + ' ep.');
  if (addr.staircase) extraParts.push(addr.staircase + ' lh.');
  if (addr.floor) extraParts.push(addr.floor + '. em.');
  if (addr.door) extraParts.push(addr.door + '. ajto');

  if (extraParts.length > 0) {
    result += ', ' + extraParts.join(' ');
  }

  return result.trim();
}

export default function AddressForm({ value, onChange, required }: AddressFormProps) {
  const [warning, setWarning] = useState('');

  useEffect(() => {
    if (value.streetName) {
      const lower = value.streetName.toLowerCase().trim();
      const hasType = KNOWN_STREET_TYPE_WORDS.some(t => lower.endsWith(' ' + t) || lower === t);
      setWarning(hasType ? 'A kozterulet jelleget a kulon mezoben valaszd ki.' : '');
    } else {
      setWarning('');
    }
  }, [value.streetName]);

  const set = (key: keyof AddressData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    onChange({ ...value, [key]: e.target.value });
  };

  return (
    <div>
      {/* Row 1: iranyitoszam + telepules */}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label className="form-label">Iranyitoszam{required ? ' *' : ''}</label>
          <input
            className="form-input"
            value={value.postalCode}
            onChange={set('postalCode')}
            placeholder="1051"
            required={required}
          />
        </div>
        <div>
          <label className="form-label">Telepules{required ? ' *' : ''}</label>
          <input
            className="form-input"
            value={value.city}
            onChange={set('city')}
            placeholder="Budapest"
            required={required}
          />
        </div>
      </div>

      {/* Row 2: kozterulet neve + jelleg + hazszam */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 100px', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label className="form-label">Kozterulet neve{required ? ' *' : ''}</label>
          <input
            className="form-input"
            value={value.streetName}
            onChange={set('streetName')}
            placeholder="Vaci"
            required={required}
          />
          {warning && (
            <div style={{ color: '#b45309', background: '#fef3c7', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', marginTop: '4px' }}>
              {warning}
            </div>
          )}
        </div>
        <div>
          <label className="form-label">Jelleg{required ? ' *' : ''}</label>
          <select
            className="form-input"
            value={value.streetType}
            onChange={set('streetType')}
            required={required}
          >
            <option value="">-- valassz --</option>
            {STREET_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">Hazszam</label>
          <input
            className="form-input"
            value={value.houseNumber}
            onChange={set('houseNumber')}
            placeholder="1."
          />
        </div>
      </div>

      {/* Row 3: epulet, lepcsohaz, emelet, ajto */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
        <div>
          <label className="form-label">Epulet</label>
          <input
            className="form-input"
            value={value.building}
            onChange={set('building')}
            placeholder="A"
          />
        </div>
        <div>
          <label className="form-label">Lepcsohaz</label>
          <input
            className="form-input"
            value={value.staircase}
            onChange={set('staircase')}
            placeholder="II."
          />
        </div>
        <div>
          <label className="form-label">Emelet</label>
          <input
            className="form-input"
            value={value.floor}
            onChange={set('floor')}
            placeholder="3."
          />
        </div>
        <div>
          <label className="form-label">Ajto</label>
          <input
            className="form-input"
            value={value.door}
            onChange={set('door')}
            placeholder="12"
          />
        </div>
      </div>
    </div>
  );
}
